import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { IntlProvider } from 'react-intl';
import { InternationalizationContext } from '../context/InternationalizationContext';
import { resolveBrowserLocale } from '../i18n';
import { log } from '../logger';
// Implement the reducer
function i18nReducer(state, action) {
    switch (action.type) {
        case 'SET_LOCALE':
            return { ...state, locale: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        default:
            return state;
    }
}
// Create the provider component
export function InternationalizationProvider({ defaultLocale, messages: providedMessages, detectBrowserLocale = false, supportedLocales, onError, loadingFallback, children, }) {
    // Consumers are expected to supply messages; fallback to empty map otherwise
    const { useEffect, useReducer, useCallback } = React;
    const effectiveMessages = providedMessages ?? {};
    // Initialize reducer
    const [state, dispatch] = useReducer(i18nReducer, {
        locale: defaultLocale,
        isLoading: false,
        error: null,
    });
    // react-intl reports formatting errors (e.g. a missing translation) while a consumer renders, so
    // this must not touch provider state: a state update there re-renders every context consumer,
    // which formats again and reports again, in a loop.
    const reportError = useCallback((error) => {
        // Log in development
        if (process.env.NODE_ENV !== 'production') {
            log.warn('[i18n] Error:', { message: error.message });
        }
        if (onError) {
            onError(error);
        }
    }, [onError]);
    // Handle errors
    const handleError = useCallback((error) => {
        reportError(error);
        dispatch({ type: 'SET_ERROR', payload: error });
    }, [reportError]);
    // Function to change locale
    const setLocale = useCallback(async (locale) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            dispatch({ type: 'SET_LOCALE', payload: locale });
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('i18n-locale', locale);
            }
        }
        catch (error) {
            handleError(error);
        }
        finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [handleError]);
    // A string, not the keys array: callers often pass `messages` inline, and an array dependency
    // would re-run the effect on every render.
    const supportedLocalesKey = (supportedLocales ?? Object.keys(effectiveMessages)).join(',');
    // Load initial locale from localStorage if available, else from the browser when enabled
    useEffect(() => {
        // Check for stored locale preference (only in browser environment)
        const storedLocale = typeof localStorage !== 'undefined' ? localStorage.getItem('i18n-locale') : null;
        if (storedLocale) {
            if (storedLocale !== defaultLocale) {
                setLocale(storedLocale);
            }
            return;
        }
        if (detectBrowserLocale && typeof navigator !== 'undefined') {
            const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
            const browserLocale = resolveBrowserLocale(browserLanguages, supportedLocalesKey.split(','));
            // Dispatched rather than setLocale(): only an explicit choice is stored, so the user still
            // follows a later change to their browser language.
            if (browserLocale && browserLocale !== defaultLocale) {
                dispatch({ type: 'SET_LOCALE', payload: browserLocale });
            }
        }
    }, [defaultLocale, detectBrowserLocale, setLocale, supportedLocalesKey]);
    // Create context value
    const contextValue = {
        locale: state.locale,
        setLocale,
        isLoading: state.isLoading,
        error: state.error,
    };
    // Show loading fallback if loading
    if (state.isLoading && loadingFallback) {
        return loadingFallback;
    }
    // Render provider
    return (_jsx(InternationalizationContext.Provider, { value: contextValue, children: _jsx(IntlProvider, { locale: state.locale, messages: effectiveMessages[state.locale] || {}, defaultLocale: defaultLocale, onError: reportError, children: children }) }));
}
